import { ModalEntry } from '@superbet-group/web.lib.vue-utilities';
import { v4 as uuidv4 } from 'uuid';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import StoreUtil from '@store/StoreUtil';

import {
    getModalEntry,
    registerModals,
} from './modalsRegistry';

type ModalCode = string;

export type ModalDequeEntry = Readonly<ModalEntry & {
    uuid: string; // Uuid to avoid re-rendering same modal also used to re-render consecutive similar yet different modals
    data?: any;
    isClosable?: boolean;
    afterClose?(): void;
}> & { shouldShowTransition?: boolean };

export interface ModalsState {
    modalsDeque: ModalDequeEntry[];
    afterCloseHook: Map<ModalCode, (dispatch: Dispatch) => void>;
}

export type ModalDequeEntryParams = Omit<ModalDequeEntry, 'uuid' | 'component'> & Partial<Pick<ModalDequeEntry, 'component'>>;

const PUSH_MODAL = 'PUSH_MODAL';
const ADD_MODAL_TO_END = 'ADD_MODAL_TO_END';
const POP_MODAL = 'POP_MODAL';
const PATCH_MODAL = 'PATCH_MODAL';
const CLEAR_MODAL_GROUP = 'CLEAR_MODAL_GROUP';
const CLEAR_MODALS = 'CLEAR_MODALS';
const ADD_AFTER_CLOSE_HOOK = 'ADD_AFTER_CLOSE_HOOK';

function defaultState(): ModalsState {
    return {
        // modals can be added to both ends, the last element is shown on top, the first element is queued
        modalsDeque: [],
        afterCloseHook: new Map(),
    };
}
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export function createModalEntry(modalParams: string | ModalDequeEntryParams, uuid: string): ModalDequeEntry {
    const modalConfig = getModalEntry(typeof modalParams === 'string' ? modalParams : modalParams.code);

    if (typeof modalParams === 'string') {
        return {
            ...modalConfig,
            uuid,
        };
    } else {
        return {
            ...modalConfig,
            ...modalParams, // We apply any custom override from parameters if they are not just the code
            uuid,
        };
    }
}

export const createModalsModule = (uuidGenerator: () => string = uuidv4) => {
    return {
        namespaced: true,

        state: defaultState(),

        mutations: {
            [PUSH_MODAL]: (state: ModalsState, modal: string | ModalDequeEntryParams) => {
                if (!modal) {
                    return;
                }
                const modalsDequeEntry = createModalEntry(modal, uuidGenerator());
                modalsDequeEntry.shouldShowTransition = false;
                if (state.modalsDeque.length === 0) {
                    modalsDequeEntry.shouldShowTransition = true;
                }
                if (modalsDequeEntry.isUnique) {
                    const currentEntryCode = modalsDequeEntry.code;
                    state.modalsDeque = state.modalsDeque.filter((existingModal: ModalDequeEntry) => {
                        return currentEntryCode !== existingModal.code;
                    });
                }
                state.modalsDeque.push(modalsDequeEntry);
            },
            [ADD_MODAL_TO_END]: (state: ModalsState, modal: string | ModalDequeEntry) => {
                if (!modal) {
                    return;
                }
                const modalsDequeEntry = createModalEntry(modal, uuidGenerator());
                state.modalsDeque.unshift(modalsDequeEntry);
            },
            [PATCH_MODAL]: (state: ModalsState, modal: ModalDequeEntry) => {
                state.modalsDeque = state.modalsDeque.map((existingModal) => {
                    if (existingModal.uuid === modal.uuid) {
                        return {
                            ...existingModal,
                            ...modal,
                        };
                    }

                    return existingModal;
                });
                const activeModal = state.modalsDeque[state.modalsDeque.length - 1];
                if (activeModal.uuid === modal.uuid) {
                    state.modalsDeque[state.modalsDeque.length - 1] = {
                        ...activeModal,
                        ...modal,
                    };
                }
            },
            [POP_MODAL]: (state: ModalsState) => {
                state.modalsDeque.pop();
                if (state.modalsDeque.length > 0) {
                    state.modalsDeque[state.modalsDeque.length - 1].shouldShowTransition = false;
                }
            },
            [CLEAR_MODAL_GROUP]: (state: ModalsState, group: string) => {
                state.modalsDeque = state.modalsDeque.filter((modal) => modal.group !== group);
            },
            [ADD_AFTER_CLOSE_HOOK]: (state: ModalsState, afterClose: () => void) => {
                const activeModalCode = state.modalsDeque[state.modalsDeque.length - 1].code;
                state.afterCloseHook.set(activeModalCode, afterClose);
            },
            [CLEAR_MODALS]: (state: ModalsState) => {
                state.modalsDeque = [];
            },
        },

        getters: {
            modal: (state: ModalsState) => state.modalsDeque[state.modalsDeque.length - 1],
            modalData: (_state: ModalsState, getters: any) => {
                if (getters.modal) {
                    return getters.modal.data || {};
                }
                return {};
            },
        },

        actions: {
            setModal: ({ commit }: { commit: Commit }, modal: string | ModalDequeEntryParams) => {
                commit(PUSH_MODAL, modal);
            },
            pushModal: StoreUtil.createSimpleMutatorAction(PUSH_MODAL),
            addModalToEnd: StoreUtil.createSimpleMutatorAction(ADD_MODAL_TO_END),
            clearModals: StoreUtil.createSimpleMutatorAction(CLEAR_MODALS),
            setIsCurrentModalClosable(context: ActionContext<ModalsState, unknown>, isClosable: boolean) {
                const activeModal = context.getters.modal;

                if (activeModal.isClosable !== isClosable) {
                    context.commit(PATCH_MODAL, {
                        ...activeModal,
                        isClosable,
                    });
                }
            },
            closeModal(context: ActionContext<ModalsState, unknown>, closeOptions?: { group?: string }) {
                /**
                 * FUTURE TODO: When the new SdsModal is closed in this way directly, via overlay tap,
                 * it will not actually run the close animation, because the inner modal is not in control.
                 * Doing that globally can be done here, but we'd need to have all modals as SdsModal, otherwise
                 * we get weird inconsistent edge cases. Until then, we have have this Frankenstein solution,
                 * where we get nice animations in just _most_ of the cases.
                 */
                if (!closeOptions?.group) {
                    const activeModal = context.getters.modal;

                    context.commit(POP_MODAL);

                    if (activeModal) {
                        context.dispatch('runAfterCloseHook', activeModal.code);
                    }
                } else {
                    const closedModalCodes = context.state.modalsDeque
                        .filter((modal) => modal.group === closeOptions!.group)
                        .map((modal) => modal.code);

                    context.commit(CLEAR_MODAL_GROUP, closeOptions.group);

                    closedModalCodes.forEach((code) => {
                        context.dispatch('runAfterCloseHook', code);
                    });
                }
            },
            addAfterCloseHook({ commit }: { commit: Commit }, afterClose: () => void) {
                commit(ADD_AFTER_CLOSE_HOOK, afterClose);
            },
            registerModals(_context: ActionContext<ModalsState, unknown>, payload: ModalEntry[]) {
                registerModals(payload);
            },
            runAfterCloseHook(context: ActionContext<ModalsState, unknown>, modalCode: ModalCode) {
                if (context.state.afterCloseHook.has(modalCode)) {
                    context.state.afterCloseHook.get(modalCode)!(context.dispatch);
                    context.state.afterCloseHook.delete(modalCode);
                }
            },
        },
    };
};

export type ModalStore = ReturnType<typeof createModalsModule>;

export default createModalsModule();
